import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useCheckout } from '@Provider/revelo-checkout';
import { useFormat } from '../../../../../helpers/hooks/useFormat';
import { ReferenceLink } from '../../../../../helpers/reference';
import FormFieldCheckbox from '../../../forms/form-field-checkbox';
import FormFieldInput from '../../../forms/form-field-input';
import { getFieldError } from '../../../utils/FormFieldUtils';
import { isValidInsuranceBirthdate, isValidInsuranceConsent } from '../../validation';

type Props = {
  minAge?: number;
  maxAge?: number;
};
const Insurance = ({ minAge, maxAge }: Props) => {
  const { formatMessage } = useFormat({ name: 'checkout' });
  const { insuranceData, setInsuranceData } = useCheckout();
  const {
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation(['checkout']);

  const fieldError = getFieldError('birthday', errors);

  const updateInsuranceData = (field: string, value: string | boolean | undefined) => {
    if (value !== undefined) {
      switch (field) {
        case 'insuranceForOrder':
          value = typeof value === 'boolean' ? value : false;
          if (value !== insuranceData.insuranceForOrder) {
            setInsuranceData({ insuranceForOrder: value });
          }
          break;
        case 'insurancePersonalDataConsent':
          value = typeof value === 'boolean' ? value : false;
          if (value !== insuranceData.personalDataConsent) {
            setInsuranceData({
              insuranceForOrder: insuranceData.insuranceForOrder ?? false,
              personalDataConsent: value,
            });
          }
          break;
        case 'insuranceParticipationConsent':
          value = typeof value === 'boolean' ? value : false;
          if (value !== insuranceData.participationConsent) {
            setInsuranceData({
              insuranceForOrder: insuranceData.insuranceForOrder ?? false,
              participationConsent: value,
            });
          }
          break;
        case 'birthday':
          value = typeof value === 'string' && value.trim().length > 0 ? value : undefined;
          if (value !== undefined && value !== insuranceData.birthday) {
            setInsuranceData({
              insuranceForOrder: insuranceData.insuranceForOrder ?? false,
              birthday: value,
            });
          }
          break;
        default:
        // nothing to do, unsupported field
      }
    }
  };

  const interpolatedComponents = [
    <ReferenceLink
      key={0}
      className="cursor-pointer text-secondary-600 hover:underline"
      target={{
        type: 'link',
        link: formatMessage({ id: 'insuranceAvbLink' }),
        openInNewWindow: true,
      }}
    />,
    <ReferenceLink
      key={1}
      className="cursor-pointer text-secondary-600 hover:underline"
      target={{
        type: 'link',
        link: formatMessage({ id: 'insuranceParticipationRequirementsLink' }),
        openInNewWindow: true,
      }}
    />,
  ];

  return (
    <div className="col-span-full flex flex-col pt-8 lg:pt-10">
      <div className="insurance-header col-span-full text-xl">
        {formatMessage({
          id: 'insuranceHeadline',
          defaultMessage: '',
        })}
      </div>
      <div
        className="mt-4 text-md"
        dangerouslySetInnerHTML={{
          __html: formatMessage({
            id: 'insuranceSubline',
            defaultMessage: '',
          }),
        }}
      ></div>
      <FormFieldCheckbox
        name="insurance"
        checked={insuranceData.insuranceForOrder}
        onChange={(checked) => updateInsuranceData('insuranceForOrder', checked)}
        containerClassNames="flex gap-2 mt-4"
        label={formatMessage({
          id: 'insuranceCheckboxLabel',
        })}
        inverseLabel
      />
      {insuranceData.insuranceForOrder && (
        <>
          <FormFieldInput
            inputType="date"
            name="birthday"
            value={insuranceData.birthday}
            label={formatMessage({
              id: 'insuranceBirthdateLabel',
            })}
            containerClassNames="mt-4"
            onChange={(name, value) => updateInsuranceData(name, value)}
            validation={{
              required: true,
              validate: {
                minAge: (value) => isValidInsuranceBirthdate(value, minAge ?? 18) || 'tooYoung',
                maxAge: (value) => isValidInsuranceBirthdate(value, undefined, maxAge ?? 120) || 'tooOld',
              },
            }}
          />
          {!fieldError && (
            <div className="px-4 text-sm text-neutral-700">
              {formatMessage({
                id: 'insuranceAge',
                defaultMessage: 'You must be at least 18 years old to be eligible for insurance coverage.',
              })}
            </div>
          )}
          <FormFieldCheckbox
            name="personalDataConsent"
            checked={insuranceData.personalDataConsent}
            onChange={(checked) => updateInsuranceData('insurancePersonalDataConsent', checked)}
            containerClassNames="flex gap-2 mt-4"
            label={formatMessage({
              id: 'insurancePersonalDataConsent',
            })}
            validation={{
              validate: (value) => isValidInsuranceConsent(value, insuranceData),
            }}
            highlightLabelOnError={true}
            inverseLabel
          />
          <FormFieldCheckbox
            name="participationConsent"
            checked={insuranceData.participationConsent}
            onChange={(checked) => updateInsuranceData('insuranceParticipationConsent', checked)}
            containerClassNames="flex gap-2 mt-4"
            label={<Trans i18nKey="insuranceParticipationConsent" t={t} components={interpolatedComponents} />}
            validation={{
              validate: (value) => isValidInsuranceConsent(value, insuranceData),
            }}
            highlightLabelOnError={true}
            inverseLabel
          />
        </>
      )}
    </div>
  );
};

export default Insurance;
